import React, { useState, useEffect } from 'react'
import { PreferenceFormModalMetaProps, Preference } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: PreferenceFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (preferenceData: any) => Promise<void>
    initialPreference?: Preference | null
}

export const PreferenceFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialPreference } = props

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        description: '',
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Populate form when editing a preference
    useEffect(() => {
        if (initialPreference && isOpen) {
            setFormData({
                name: initialPreference.name,
                category: initialPreference.category || '',
                description: initialPreference.description || '',
            })
        } else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                category: '',
                description: '',
            })
            setError(null)
        }
    }, [initialPreference, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            await onSubmit(formData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="preference-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="preference-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                        {/* Preference Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.nameLabel} <span className="text-danger-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.namePlaceholder}
                            />
                        </div>

                        {/* Category */}
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.categoryLabel}
                            </label>
                            <input
                                type="text"
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.categoryPlaceholder}
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.descriptionLabel}
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows={3}
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.descriptionPlaceholder}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Modal >
    )
}
